import { useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import { Landmark, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BankgiroEntryForm } from "@/components/invoiceGeneration/BankgiroEntryForm";
import { BankgiroReplacementNotice } from "@/components/invoiceGeneration/BankgiroReplacementNotice";
import { invoiceProvider, type Seller } from "@/services/invoiceGeneration";

/**
 * Company settings → Payment details (FIN-818 S2): the company's bankgiro by number alone
 * (FIN-1172), then each connected account's own bankgiro.
 */
export const CompanyBankgiroSettings = () => {
  const t = useTranslations("consultantSettings.bankgiro");
  const [seller, setSeller] = useState<Seller | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const apply = async (pending: Promise<Seller>) => {
    setSeller(await pending);
    setEditingId(null);
  };

  useEffect(() => {
    let alive = true;
    invoiceProvider.getSeller()
      .then((s) => { if (alive) setSeller(s); })
      .catch(() => { if (alive) setLoadFailed(true); });
    return () => { alive = false; };
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Landmark className="h-5 w-5" />
          {t("title")}
        </CardTitle>
        <CardDescription>{t("description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loadFailed && <p className="text-sm text-destructive">{t("loadFailed")}</p>}
        {!loadFailed && !seller && <Loader2 className="mx-auto h-6 w-6 animate-spin" />}
        {seller?.bankgiroReplacementNotice && (
          <BankgiroReplacementNotice
            notice={seller.bankgiroReplacementNotice}
            onDismiss={async (id) => setSeller(await invoiceProvider.dismissBankgiroNotice(id))}
          />
        )}
        {seller && (
          <ul className="divide-y">
            <BankgiroRow id={COMPANY} label={t("company")} bankgiro={seller.companyBankgiro ?? null} manual
              removeConsequence={t("removeCompanyConsequence")}
              editingId={editingId} setEditingId={setEditingId}
              onSave={(typed) => apply(invoiceProvider.setCompanyBankgiro(typed))}
              onRemove={() => apply(invoiceProvider.removeCompanyBankgiro())} />
            {seller.invoiceBankAccounts.map((account) => {
              const label = `${account.bankName} ${account.maskedAccountNumber}`;
              return (
                <BankgiroRow key={account.id} id={account.id} label={label} bankgiro={account.bankgiro}
                  manual={account.bankgiroProvenance === "manual"}
                  removeConsequence={t("removeAccountConsequence", { label })}
                  editingId={editingId} setEditingId={setEditingId}
                  onSave={(typed) => apply(invoiceProvider.setBankgiro(account.id, typed))}
                  onRemove={() => apply(invoiceProvider.removeBankgiro(account.id))} />
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

const COMPANY = "company";

function BankgiroRow({ id, label, bankgiro, manual, removeConsequence, editingId, setEditingId, onSave, onRemove }: {
  id: string;
  label: string;
  bankgiro: string | null;
  /** Typed in, so it can be changed or removed; a bank-reported one is read-only. */
  manual: boolean;
  removeConsequence: string;
  editingId: string | null;
  setEditingId: (id: string | null) => void;
  onSave: (typed: string) => Promise<void>;
  onRemove: () => Promise<void>;
}) {
  const t = useTranslations("consultantSettings.bankgiro");
  const editing = editingId === id;
  const onEdit = () => setEditingId(id);
  const onClose = () => setEditingId(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeFailed, setRemoveFailed] = useState(false);

  const remove = async () => {
    setRemoving(true);
    setRemoveFailed(false);
    try {
      await onRemove();
    } catch {
      setRemoveFailed(true);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <li className="py-3">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{label}</p>
          <p className="text-xs text-muted-foreground">{bankgiro ? t("number", { number: bankgiro }) : t("none")}</p>
        </div>
        {bankgiro && (
          <Badge variant={manual ? "outline" : "secondary"}>{manual ? t("addedByYou") : t("fromBank")}</Badge>
        )}
        {!editing && manual && bankgiro && <Button type="button" size="sm" variant="ghost" onClick={onEdit}>{t("change")}</Button>}
        {!editing && !bankgiro && <Button type="button" size="sm" variant="outline" onClick={onEdit}>{t("add")}</Button>}
      </div>
      {editing && (
        <div>
          <BankgiroEntryForm
            initialValue={manual ? bankgiro ?? "" : ""}
            onSave={onSave}
            onCancel={() => { setConfirmingRemove(false); onClose(); }}
          />
          {manual && bankgiro && !confirmingRemove && (
            <Button type="button" size="sm" variant="ghost" className="mt-2 text-destructive" onClick={() => setConfirmingRemove(true)}>
              {t("remove")}
            </Button>
          )}
          {confirmingRemove && (
            <div className="mt-2 rounded-md border p-3 text-sm">
              <p className="font-medium">{t("removeTitle", { number: bankgiro })}</p>
              <p className="text-muted-foreground">{t("removeNote", { consequence: removeConsequence })}</p>
              {removeFailed && <p role="alert" className="text-destructive">{t("removeFailed")}</p>}
              <div className="mt-2 flex gap-2">
                <Button type="button" size="sm" variant="destructive" disabled={removing} onClick={remove}>
                  {removing && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                  {t("removeAction")}
                </Button>
                <Button type="button" size="sm" variant="ghost" disabled={removing} onClick={() => setConfirmingRemove(false)}>
                  {t("keep")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
